import React, { useState, useEffect, useRef } from 'react';
import { SimulationTemplate, SimulationStep, ResultBlock, GraphContainer, ResultMessage } from '../SimulationTemplate';
import { Text } from '../../ui/typography/Text';
import { AccentButton } from '../../ui/buttons/AccentButton';
import { OutlineButton } from '../../ui/buttons/OutlineButton';
import { Brain, BookOpen, Microscope, BarChart3 } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

// Historical events for the simulation
const historicalEvents = [
  {
    id: 1,
    scenario: "In 1954, researchers conducted a study where they exposed children to violent or non-violent media.",
    question: "What effect did this have on the children's behavior?",
    options: [
      { id: 'a', text: "No measurable effect on behavior" },
      { id: 'b', text: "Children exposed to violent media became less aggressive" },
      { id: 'c', text: "Children exposed to violent media became more aggressive" },
      { id: 'd', text: "Only affected children with pre-existing behavioral issues" }
    ],
    correctAnswer: 'c',
    correctText: "Children exposed to violent media became more aggressive (Bandura's Bobo doll experiment)"
  },
  {
    id: 2,
    scenario: "In 1962, a major automotive manufacturer launched a new compact car model despite internal concerns about its safety.",
    question: "What happened after the car was released?",
    options: [
      { id: 'a', text: "It became one of the safest vehicles in its class" },
      { id: 'b', text: "Sales were poor but no safety issues emerged" },
      { id: 'c', text: "Multiple fatal accidents occurred due to design flaws" },
      { id: 'd', text: "The company voluntarily recalled it before incidents occurred" }
    ],
    correctAnswer: 'c',
    correctText: "Multiple fatal accidents occurred due to design flaws (Ford Pinto case)"
  }
];

// Interface for user responses
interface UserResponse {
  eventId: number;
  likelihoodBeforeOutcome: number | null;
  likelihoodAfterOutcome: number | null;
  selectedOption: string | null;
}

const TemplatedHindsightBiasSimulation: React.FC = () => {
  // ========== STATE MANAGEMENT ==========
  const [started, setStarted] = useState(false);
  const [stage, setStage] = useState<'intro' | 'beforeOutcome' | 'afterOutcome' | 'results'>('intro');
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [userResponses, setUserResponses] = useState<UserResponse[]>([
    { eventId: 1, likelihoodBeforeOutcome: null, likelihoodAfterOutcome: null, selectedOption: null },
    { eventId: 2, likelihoodBeforeOutcome: null, likelihoodAfterOutcome: null, selectedOption: null }
  ]);
  const [showResults, setShowResults] = useState(false);
  const [showOutcome, setShowOutcome] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  // Current event
  const currentEvent = historicalEvents[currentEventIndex];

  // References for scrolling
  const simulationRef = useRef<HTMLDivElement>(null);
  const beforeOutcomeRef = useRef<HTMLDivElement>(null);
  const afterOutcomeRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  
  // ========== EFFECT HOOKS ==========
  // Scroll to the active step whenever the stage changes
  useEffect(() => {
    if (stage === 'beforeOutcome' && beforeOutcomeRef.current) {
      beforeOutcomeRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (stage === 'afterOutcome' && afterOutcomeRef.current) {
      afterOutcomeRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (stage === 'results' && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [stage, currentEventIndex]);

  // Reset selected option when moving to a new event
  useEffect(() => {
    setSelectedOption(userResponses[currentEventIndex].selectedOption);
  }, [currentEventIndex, userResponses]);

  // ========== EVENT HANDLERS ==========
  // Handle user selecting an option
  const handleSelectOption = (optionId: string) => {
    setSelectedOption(optionId);
    const updatedResponses = [...userResponses];
    updatedResponses[currentEventIndex].selectedOption = optionId;
    setUserResponses(updatedResponses);
  };

  // Handle user rating likelihood before seeing outcome
  const handleLikelihoodBeforeOutcome = (likelihood: number) => {
    const updatedResponses = [...userResponses];
    updatedResponses[currentEventIndex].likelihoodBeforeOutcome = likelihood;
    setUserResponses(updatedResponses);
  };

  // Handle user rating likelihood after seeing outcome
  const handleLikelihoodAfterOutcome = (likelihood: number) => {
    const updatedResponses = [...userResponses];
    updatedResponses[currentEventIndex].likelihoodAfterOutcome = likelihood;
    setUserResponses(updatedResponses);
  };

  // Move to next stage or event
  const handleNext = () => {
    if (stage === 'intro') {
      setStage('beforeOutcome');
    } else if (stage === 'beforeOutcome') {
      setShowOutcome(true);
      setStage('afterOutcome');
    } else if (stage === 'afterOutcome') {
      if (currentEventIndex < historicalEvents.length - 1) {
        setCurrentEventIndex(currentEventIndex + 1);
        setShowOutcome(false);
        setStage('beforeOutcome');
      } else {
        setStage('results');
        setShowResults(true);
      }
    }
  };

  // Reset the simulation
  const handleReset = () => {
    setStarted(false);
    setStage('intro');
    setCurrentEventIndex(0);
    setUserResponses([
      { eventId: 1, likelihoodBeforeOutcome: null, likelihoodAfterOutcome: null, selectedOption: null },
      { eventId: 2, likelihoodBeforeOutcome: null, likelihoodAfterOutcome: null, selectedOption: null }
    ]);
    setShowResults(false);
    setShowOutcome(false);
    setSelectedOption(null);
    
    // Scroll to the top of the simulation
    simulationRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ========== SIMULATION-SPECIFIC DATA ==========
  const simulationData = {
    title: "Hindsight Bias",
    description: "Experience how knowing the outcome of an event affects your perception of its predictability. This simulation recreates the classic 'knew-it-all-along' effect studied by Fischhoff.",
    citation: "Fischhoff (1975), \"Hindsight ≠ Foresight: The Effect of Outcome Knowledge on Judgment Under Uncertainty,\" Journal of Experimental Psychology: Human Perception and Performance"
  };

  // ========== HELPER FUNCTIONS ==========
  // Calculate hindsight bias metrics
  const calculateHindsightBias = () => {
    const differences = userResponses.map(response => {
      if (response.likelihoodBeforeOutcome !== null && response.likelihoodAfterOutcome !== null) {
        return {
          eventId: response.eventId,
          difference: response.likelihoodAfterOutcome - response.likelihoodBeforeOutcome
        };
      }
      return { eventId: response.eventId, difference: 0 };
    });

    const averageDifference = differences.reduce((sum, item) => sum + item.difference, 0) / differences.length;
    
    return {
      differences,
      averageDifference
    };
  };

  // Prepare chart data
  const getChartData = () => {
    const biasMetrics = calculateHindsightBias();
    
    const differenceData = biasMetrics.differences.map(item => ({
      name: `Scenario ${item.eventId}`,
      difference: item.difference
    }));
    
    return differenceData;
  };
  
  // Render likelihood slider with mobile optimization
  const renderLikelihoodSlider = (value: number | null, onChange: (value: number) => void, label: string) => (
    <div className="w-full space-y-2">
      <Text className="text-gray-700 text-sm sm:text-base">{label}</Text>
      <input
        type="range"
        min="0"
        max="100"
        value={value || 50}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
      />
      <div className="flex justify-between">
        <Text size="sm" className="text-gray-500 text-xs sm:text-sm">0%</Text>
        <Text size="sm" className="text-gray-500 text-xs sm:text-sm">{value || 50}%</Text>
        <Text size="sm" className="text-gray-500 text-xs sm:text-sm">100%</Text>
      </div>
    </div>
  );

  // ========== COMPONENT CONTENT ==========
  const simulationContent = (
    <div ref={simulationRef} className="space-y-8">
      {/* Before Outcome Stage */}
      {stage === 'beforeOutcome' && (
        <div ref={beforeOutcomeRef}>
          <SimulationStep title={`Scenario ${currentEventIndex + 1} of ${historicalEvents.length}`} isActive={true}>
            <div className="space-y-4">
              <Text className="font-medium text-gray-800 text-sm sm:text-base">
                {currentEvent.scenario}
              </Text>
              <Text className="text-gray-700 font-medium text-sm sm:text-base">
                {currentEvent.question}
              </Text>
              
              <div className="grid grid-cols-1 gap-2">
                {currentEvent.options.map(option => (
                  <OutlineButton
                    key={option.id}
                    onClick={() => handleSelectOption(option.id)}
                    active={selectedOption === option.id}
                    className={`py-2 px-3 text-sm`}
                  >
                    {option.text}
                  </OutlineButton>
                ))}
              </div>
              
              {selectedOption && (
                <div className="pt-3 space-y-4">
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <Text className="text-gray-700 text-sm">
                      You selected: <span className="font-medium">{currentEvent.options.find(o => o.id === selectedOption)?.text}</span>
                    </Text>
                  </div>
                  
                  {renderLikelihoodSlider(
                    userResponses[currentEventIndex].likelihoodBeforeOutcome,
                    handleLikelihoodBeforeOutcome,
                    `How likely do you think your selected outcome is correct?`
                  )}
                  
                  <div className="flex justify-center pt-2">
                    <AccentButton 
                      onClick={handleNext}
                      disabled={userResponses[currentEventIndex].likelihoodBeforeOutcome === null}
                      className="text-sm py-2"
                    >
                      Reveal Actual Outcome
                    </AccentButton>
                  </div>
                </div>
              )}
            </div>
          </SimulationStep>
        </div>
      )}

      {/* After Outcome Stage */}
      {stage === 'afterOutcome' && (
        <div ref={afterOutcomeRef}>
          <SimulationStep title={`Outcome of Scenario ${currentEventIndex + 1}`} isActive={true}>
            <div className="space-y-4">
              <Text className="font-medium text-gray-800 text-sm sm:text-base">
                {currentEvent.scenario}
              </Text>
              
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                <Text className="text-blue-800 font-medium text-sm">
                  The actual outcome: {currentEvent.correctText}
                </Text>
              </div>
              
              <div className="grid grid-cols-1 gap-2">
                {currentEvent.options.map(option => (
                  <div 
                    key={option.id} 
                    className={`p-2 rounded-lg border text-sm ${
                      option.id === currentEvent.correctAnswer 
                        ? 'bg-green-50 border-green-200' 
                        : option.id === selectedOption && option.id !== currentEvent.correctAnswer
                          ? 'bg-red-50 border-red-200'
                          : 'bg-white border-gray-100'
                    }`}
                  >
                    <Text className={`text-sm ${
                      option.id === currentEvent.correctAnswer 
                        ? 'text-green-800' 
                        : option.id === selectedOption && option.id !== currentEvent.correctAnswer
                          ? 'text-red-800'
                          : 'text-gray-700'
                    }`}>
                      {option.text} {option.id === currentEvent.correctAnswer && '✓'}
                    </Text>
                  </div>
                ))}
              </div>
              
              <Text className="text-gray-700 text-sm">
                Now that you know what actually happened, please rate again:
              </Text>
              
              <div className="pt-2">
                {renderLikelihoodSlider(
                  userResponses[currentEventIndex].likelihoodAfterOutcome,
                  handleLikelihoodAfterOutcome,
                  "How predictable was this outcome in hindsight?"
                )}
                
                <div className="flex justify-center pt-4">
                  <AccentButton 
                    onClick={handleNext}
                    disabled={userResponses[currentEventIndex].likelihoodAfterOutcome === null}
                    className="text-sm py-2"
                  >
                    {currentEventIndex < historicalEvents.length - 1 ? 'Next Scenario' : 'See Results'}
                  </AccentButton>
                </div>
              </div>
            </div>
          </SimulationStep>
        </div>
      )}

      {/* RESULTS SECTION */}
      {showResults && (
        <div ref={resultsRef} className="space-y-6">
          {/* Understanding Results Block */}
          <ResultBlock
            title="Understanding the Results"
            icon={Brain}
            variant="highlight"
          >
            <div className="space-y-8">
              {/* Step 1: User Results */}
              <div className="space-y-5">
                <Text className="text-gray-800 font-medium text-lg">
                  Your Results
                </Text>
                
                {calculateHindsightBias().averageDifference >= 0 ? (
                  <ResultMessage 
                    isCorrect={false} 
                    message={`Your average hindsight bias: ${calculateHindsightBias().averageDifference.toFixed(1)}%. In Fischhoff's original study, participants showed an average increase of 15-20% in their likelihood ratings. Most people show this bias - even when they're aware of it or try to avoid it. This demonstrates how outcomes influence our perception of how predictable events really are.`}
                  />
                ) : (
                  <ResultMessage 
                    isCorrect={true} 
                    message={`Your average hindsight bias: ${calculateHindsightBias().averageDifference.toFixed(1)}%. Interestingly, you showed a negative hindsight bias, meaning you rated outcomes as less predictable after knowing them. This is uncommon - most participants in Fischhoff's original study showed a positive bias of 15-20%. This demonstrates how outcomes influence our perception of how predictable events really are.`}
                  />
                )}
                
                <Text className="text-gray-700">
                  Hindsight bias is the tendency to see events as more predictable 
                  after you know the outcome, creating an illusion that we "knew it all along".
                  {calculateHindsightBias().averageDifference < 0 
                      ? " Your negative result suggests you may be consciously compensating for this bias, which is unusual but shows awareness of how hindsight can distort judgment."
                      : ""}
                </Text>
                
                <GraphContainer>
                  <div className="h-56 sm:h-64 -mx-4 sm:mx-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={getChartData()}
                        margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                        <YAxis 
                          tick={{ fontSize: 10 }} 
                          label={{ 
                            value: 'Percentage Increase', 
                            angle: -90, 
                            position: 'insideLeft',
                            style: { textAnchor: 'middle', fontSize: 12 }
                          }}
                        />
                        <Tooltip 
                          formatter={(value) => [`${value}%`, 'Hindsight Bias']}
                          wrapperClassName="hidden sm:block"
                        />
                        <Bar 
                          dataKey="difference" 
                          fill="#f97316"
                          label={{
                            position: 'top',
                            formatter: (value: number) => `${value}%`,
                            fontSize: 10
                          }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </GraphContainer>
                
                <Text className="text-gray-700 text-sm">
                  This chart shows the difference between your likelihood ratings before and after knowing the outcome. 
                  Positive values indicate hindsight bias.
                </Text>
              </div>
              
              {/* Step 2: Connection to the Bias */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  How This Relates to Hindsight Bias
                </Text>
                <Text className="text-gray-700">
                  Hindsight bias affects many areas of life. In medicine, doctors may overestimate how obvious a diagnosis was. 
                  In legal settings, jurors often believe defendants should have foreseen negative outcomes.
                </Text>
                <Text className="text-gray-700">
                  This bias can lead to:
                </Text>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Overconfidence in decision-making</li>
                  <li>Unfair blame for others not foreseeing problems</li>
                  <li>Failure to learn from past experiences</li>
                  <li>Poor risk assessment in future situations</li>
                </ul>
              </div>
              
              {/* Step 3: Study Findings */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  What the Original Study Found
                </Text>
                <Text className="text-gray-700">
                  In Baruch Fischhoff's 1975 study "Hindsight ≠ Foresight," participants read passages about historical events 
                  unfamiliar to them. Some were told the outcomes while others weren't.
                </Text>
                <Text className="text-gray-700">
                  The study revealed:
                </Text>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Participants who knew the outcome rated it as significantly more likely</li>
                  <li>They believed they would have predicted it even without being told</li>
                  <li>When instructed to ignore outcome information, they were unable to do so</li>
                  <li>The average increase in likelihood ratings was 15-20% after learning outcomes</li>
                </ul>
                <Text className="text-gray-700">
                  These findings suggest that hindsight bias is a robust cognitive phenomenon that impacts 
                  how we assess the predictability of past events, even when we're trying to be objective.
                </Text>
              </div>
            </div>
          </ResultBlock>

          {/* About the Study Block */}
          <ResultBlock
            title="About the Study"
            icon={BookOpen}
            variant="muted"
          >
            <div className="space-y-4">
              <Text className="text-gray-700">
                Based on research by {simulationData.citation}. This classic study demonstrated how our perception of events 
                is systematically distorted by knowledge of outcomes.
              </Text>
              
              <Text className="text-sm text-gray-500 italic">
                This simulation is designed for educational purposes. Individual results may vary.
              </Text>
            </div>
          </ResultBlock>

          {/* Try Again Button */}
          <div className="flex justify-center">
            <AccentButton onClick={handleReset}>
              Try Again
            </AccentButton>
          </div>
        </div>
      )}
    </div>
  );

  // ========== MAIN COMPONENT RENDER ==========
  return (
    <SimulationTemplate
      title={simulationData.title}
      description={simulationData.description}
    >
      {!started ? (
        <div className="flex justify-center">
          <AccentButton onClick={() => {
            setStarted(true);
            setStage('beforeOutcome');
          }}>
            Start Simulation
          </AccentButton>
        </div>
      ) : (
        simulationContent
      )}
    </SimulationTemplate>
  );
};

export default TemplatedHindsightBiasSimulation; 